import { useState, useEffect } from 'react';
import { Layout } from '../components/common/Layout';
import { bookService } from '../services/bookService';
import { bookRecordService } from '../services/bookRecordService';
import type { Book, BookRecord, MustReadBook, RecordStatus } from '../types';
import { getImageUrl } from '../utils/imageHelper';
import { searchBookRecordAdvanced } from '../utils/search';
import { sortBookRecords } from '../utils/records';
import './DashboardPage.css';

const ITEMS_PER_PAGE = 10;
const TOP_BOOKS_LIMIT = 10;

type DashboardTab = '' | RecordStatus | 'latest' | 'must-read';

const isActivityTab = (tab: DashboardTab): tab is '' | RecordStatus =>
  tab === '' || tab === 'borrowed' || tab === 'returned' || tab === 'lost';

export const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalBooks: 0,
    currentlyBorrowed: 0,
    totalBorrowers: 0,
    availableBooks: 0,
  });
  const [books, setBooks] = useState<Book[]>([]);
  const [latestBooks, setLatestBooks] = useState<Book[]>([]);
  const [mustReadBooks, setMustReadBooks] = useState<MustReadBook[]>([]);
  const [recentActivity, setRecentActivity] = useState<BookRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<DashboardTab>('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const [booksData, records, recentBooks, mustRead] = await Promise.all([
        bookService.getAllBooks({ limit: 1000 }),
        bookRecordService.getAllRecords({ limit: 1000 }),
        bookService.getRecentBooks(TOP_BOOKS_LIMIT),
        bookService.getMustReadBooks(TOP_BOOKS_LIMIT),
      ]);

      setBooks(booksData);
      setLatestBooks(recentBooks);
      setMustReadBooks(mustRead);
      setStats({
        totalBooks: booksData.length,
        currentlyBorrowed: records.filter(r => r.status === 'borrowed').length,
        totalBorrowers: new Set(records.map(r => r.borrower_name)).size,
        availableBooks: booksData.length - records.filter(r => r.status === 'borrowed').length,
      });

      setRecentActivity(records);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getBookTitle = (record: BookRecord) => {
    return record.book_title || record.book_sn || `Book ID: ${record.book_id}`;
  };

  const activityStatusFilter: RecordStatus | '' =
    isActivityTab(activeTab) && activeTab !== 'lost' ? activeTab : '';

  const filteredActivity = sortBookRecords(
    recentActivity.filter(record => {
      const book = books.find(b => b.id === record.book_id);
      const matchesSearch = searchBookRecordAdvanced(searchTerm, record, book);
      const matchesStatus = !activityStatusFilter || record.status === activityStatusFilter;
      return matchesSearch && matchesStatus;
    }),
    activityStatusFilter
  );

  const searchBookList = (book: Book) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      book.title.toLowerCase().includes(term) ||
      book.author_name.toLowerCase().includes(term) ||
      (book.category?.toLowerCase().includes(term) ?? false) ||
      (book.isbn?.toLowerCase().includes(term) ?? false)
    );
  };

  const filteredLatestBooks = latestBooks.filter(searchBookList);
  const filteredMustReadBooks = mustReadBooks.filter(searchBookList);

  const totalPages = Math.ceil(filteredActivity.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedActivity = filteredActivity.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderBookCoverCell = (book: Book, title: string) => {
    const coverUrl = getImageUrl(book.image_path);
    return (
      <td className="book-cover-column">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={title}
            className="book-cover-inline"
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.display = 'none';
              const placeholder = target.nextElementSibling as HTMLElement;
              if (placeholder) {
                placeholder.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div
          className="book-cover-placeholder-inline"
          style={{ display: coverUrl ? 'none' : 'flex' }}
          title={title}
        >
          <span className="material-symbols-outlined">menu_book</span>
        </div>
      </td>
    );
  };

  const sectionTitle =
    activeTab === 'latest'
      ? 'Latest Top 10'
      : activeTab === 'must-read'
        ? 'Must Read'
        : 'Recent Borrowing Activity';

  const searchPlaceholder =
    activeTab === 'latest' || activeTab === 'must-read'
      ? 'Search by title, author, category, or ISBN...'
      : 'Search by borrower name or book title...';

  if (isLoading) {
    return (
      <Layout isAdmin>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout isAdmin>
      <div className="dashboard-page">
        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon-primary">
              <span className="material-symbols-outlined">library_books</span>
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Books</p>
              <p className="stat-value">{stats.totalBooks.toLocaleString()}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-blue">
              <span className="material-symbols-outlined">assignment_returned</span>
            </div>
            <div className="stat-content">
              <p className="stat-label">Currently Borrowed</p>
              <p className="stat-value">{stats.currentlyBorrowed.toLocaleString()}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-green">
              <span className="material-symbols-outlined">auto_stories</span>
            </div>
            <div className="stat-content">
              <p className="stat-label">Available Books</p>
              <p className="stat-value">{stats.availableBooks.toLocaleString()}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-purple">
              <span className="material-symbols-outlined">group</span>
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Borrowers</p>
              <p className="stat-value">{stats.totalBorrowers.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Unified activity / latest / must-read section */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">{sectionTitle}</h3>
            <div className="header-actions">
              <div className="filter-tabs">
                <button
                  className={`filter-tab ${activeTab === '' ? 'active' : ''}`}
                  onClick={() => setActiveTab('')}
                >
                  All
                </button>
                <button
                  className={`filter-tab ${activeTab === 'borrowed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('borrowed')}
                >
                  Borrowed
                </button>
                <button
                  className={`filter-tab ${activeTab === 'returned' ? 'active' : ''}`}
                  onClick={() => setActiveTab('returned')}
                >
                  Returned
                </button>
                <button
                  className={`filter-tab ${activeTab === 'latest' ? 'active' : ''}`}
                  onClick={() => setActiveTab('latest')}
                >
                  Latest
                </button>
                <button
                  className={`filter-tab ${activeTab === 'must-read' ? 'active' : ''}`}
                  onClick={() => setActiveTab('must-read')}
                >
                  Must Read
                </button>
              </div>
              <div className="search-wrapper">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="text"
                  className="form-input search-input"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {activeTab === 'latest' ? (
            <>
              <div className="table-container">
                <table className="table books-ranking-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Cover</th>
                      <th>Title</th>
                      <th>Author</th>
                      <th>Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLatestBooks.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="empty-state">
                          No latest books found
                        </td>
                      </tr>
                    ) : (
                      filteredLatestBooks.map((book, index) => (
                        <tr key={book.id}>
                          <td className="rank-cell">{index + 1}</td>
                          {renderBookCoverCell(book, book.title)}
                          <td className="book-title-column">
                            <span className="book-title-text">{book.title}</span>
                          </td>
                          <td className="text-sub">{book.author_name}</td>
                          <td className="text-sub">{formatDate(book.added_date)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <p className="pagination-info">
                  Showing <strong>{filteredLatestBooks.length}</strong> of{' '}
                  <strong>top {TOP_BOOKS_LIMIT}</strong> latest books
                </p>
              </div>
            </>
          ) : activeTab === 'must-read' ? (
            <>
              <div className="table-container">
                <table className="table books-ranking-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Cover</th>
                      <th>Title</th>
                      <th>Author</th>
                      <th>Readers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMustReadBooks.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="empty-state">
                          No must-read books found
                        </td>
                      </tr>
                    ) : (
                      filteredMustReadBooks.map((book, index) => (
                        <tr key={book.id}>
                          <td className="rank-cell">{index + 1}</td>
                          {renderBookCoverCell(book, book.title)}
                          <td className="book-title-column">
                            <span className="book-title-text">{book.title}</span>
                          </td>
                          <td className="text-sub">{book.author_name}</td>
                          <td>
                            <span className="reader-count-badge">
                              <span className="material-symbols-outlined">group</span>
                              {book.reader_count} {book.reader_count === 1 ? 'reader' : 'readers'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <p className="pagination-info">
                  Showing <strong>{filteredMustReadBooks.length}</strong> of{' '}
                  <strong>top {TOP_BOOKS_LIMIT}</strong> must-read books
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Borrower Name</th>
                      <th>Cover</th>
                      <th>Title</th>
                      <th>Borrow Date</th>
                      <th>Return Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedActivity.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="empty-state">
                          No recent activity found
                        </td>
                      </tr>
                    ) : (
                      paginatedActivity.map((record) => {
                        const book = books.find(b => b.id === record.book_id);
                        const coverUrl = getImageUrl(book?.image_path);
                        return (
                          <tr key={record.id}>
                            <td>
                              <div className="borrower-cell">
                                <div className="avatar">
                                  {getInitials(record.borrower_name)}
                                </div>
                                <span>{record.borrower_name}</span>
                              </div>
                            </td>
                            <td className="book-cover-column">
                              {coverUrl ? (
                                <img
                                  src={coverUrl}
                                  alt="Book cover"
                                  className="book-cover-inline"
                                  onError={(e) => {
                                    const target = e.currentTarget as HTMLImageElement;
                                    target.style.display = 'none';
                                    const placeholder = target.nextElementSibling as HTMLElement;
                                    if (placeholder) {
                                      placeholder.style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <div className="book-cover-placeholder-inline" style={{ display: coverUrl ? 'none' : 'flex' }} title={getBookTitle(record)}>
                                <span className="material-symbols-outlined">menu_book</span>
                              </div>
                            </td>
                            <td className="book-title-column">
                              <span className="book-title-text">{getBookTitle(record)}</span>
                            </td>
                            <td className="text-sub">{formatDate(record.borrowed_date)}</td>
                            <td className="text-sub">{record.return_date ? formatDate(record.return_date) : '-'}</td>
                            <td>
                              <span className={`status-badge status-${record.status}`}>
                                {record.status === 'borrowed' ? 'Borrowed' : record.status === 'returned' ? 'Returned' : record.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="table-footer">
                <p className="pagination-info">
                  Showing <strong>{filteredActivity.length === 0 ? 0 : startIndex + 1}</strong> to{' '}
                  <strong>{Math.min(endIndex, filteredActivity.length)}</strong> of{' '}
                  <strong>{filteredActivity.length}</strong> recent activities
                </p>
                <div className="pagination-buttons">
                  <button
                    className="btn btn-ghost"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <span className="material-symbols-outlined">chevron_left</span>
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    <span className="material-symbols-outlined">chevron_right</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};
